/**
 * The Training guide's revision, worked out from the change notes in
 * `docs/training-changes/` instead of typed into `content/index.ts`.
 *
 * It used to be hand-set, together with a changelog comment and a fingerprint in
 * `access.spec.ts`, so every training PR edited the same three spots and the second
 * one to merge always conflicted and had to be renumbered. Now a PR ADDS a file and
 * edits nothing shared: two PRs add two different files, they cannot conflict, and
 * the number falls out of merge order.
 *
 *   revision = `0.` + (BASELINE_REVISION + the number of notes)
 *   revised  = the latest date in a note's filename (or the baseline's)
 *
 * ⚠️ WHAT MUST STAY TRUE. `TRAINING.revision` is stored with each reader's ticked
 * topics and compared by string EQUALITY to flag a stale tick (trainingReport.ts,
 * TopicProgress.tsx). So the string has to change whenever reader-visible content
 * changes, and a value that has been issued must never be issued again for different
 * content. Both follow from the count only ever going up:
 *
 *   - a PR that changes the guide adds a note (the CI gate, scripts/lib/training-revision.mjs);
 *   - notes are append-only — the gate refuses a PR that deletes or renames one,
 *     because that would lower the count and, once new notes arrive, re-issue a
 *     revision readers have already ticked against.
 *
 * Revisions 0.1–0.37 predate this and keep their values: the baseline IS 0.37, and
 * the hand-written changelog for 0.1–0.37 stays as a comment in content/index.ts.
 *
 * Pure on purpose — no file system, no Vite. The listing lives in
 * `apps/web/vite/trainingChangeNotes.ts`, which reads the directory at build time and
 * hands the bundle the FILENAMES ONLY. The notes' text never ships.
 */

/** The last revision that was numbered by hand. Never edited again. */
export const BASELINE_REVISION = 37;
/** When the baseline was reviewed. Also a floor for `revised`. */
export const BASELINE_REVISED = '2026-10-07';

export interface ChangeNote {
  file: string;
  /** `YYYY-MM-DD`, from the filename. */
  date: string;
  slug: string;
}

/**
 * `YYYY-MM-DD-slug.md`. The README recommends putting the issue or PR number in
 * the slug (`2026-10-07-571-equipment-rental.md`) so two PRs cannot pick the same
 * name, but it is not enforced: a descriptive slug is enough in practice, and a
 * collision is a visible add/add conflict whose fix is a rename.
 */
const NOTE_NAME = /^(\d{4})-(\d{2})-(\d{2})-([a-z0-9]+(?:-[a-z0-9]+)*)\.md$/;

/** Null when `file` is not a well-formed note name, or its date is not a real one. */
export function parseNoteName(file: string): ChangeNote | null {
  const m = NOTE_NAME.exec(file);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // A real calendar date: Date rolls 2026-02-30 over to March, so round-trip it.
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) {
    return null;
  }
  return { file, date: `${m[1]}-${m[2]}-${m[3]}`, slug: m[4] };
}

/**
 * The revision for a set of counted notes. Throws on a malformed or repeated name
 * rather than skipping it: a note that is silently not counted is exactly the
 * failure this scheme exists to prevent, so it has to be loud.
 */
export function deriveRevision(files: readonly string[]): { revision: string; revised: string } {
  if (new Set(files).size !== files.length) {
    throw new Error('Training change notes: the same filename was listed twice.');
  }
  let revised = BASELINE_REVISED;
  for (const file of files) {
    const note = parseNoteName(file);
    if (!note) {
      throw new Error(
        `Training change notes: "${file}" is not a valid note name. Use YYYY-MM-DD-slug.md `
        + `(lowercase letters, digits and hyphens, a real date).`,
      );
    }
    if (note.date > revised) revised = note.date;
  }
  return { revision: `0.${BASELINE_REVISION + files.length}`, revised };
}
